<template>
    <div v-if="type === 'perJaw'">
        <div v-if="source === 'caseDetail'" class="detail-img-box">
            <div class="detail-img" :class="showJawImg">
                <div class="detail-img-text upper-text" :class="showJawImg === 'lower-jaw' ? 'disabled-text' : ''">{{ $t('design.toothUpperJaw') || '上颌' }}</div>
                <div class="detail-img-text lower-text" :class="showJawImg === 'upper-jaw' ? 'disabled-text' : ''">{{ $t('design.toothLowerJaw') || '下颌' }}</div>
            </div>
        </div>
        <div v-else>
            <div class="uplower-wrap" :class="disabled ? 'uplower-wrap-disabled' : ''">
                <div class="item" :class="upCheckValue ? 'item-active' : ''" @click="() => onToggle('up')">
                    <div>
                        <span @click.stop="onCheckBox">
                            <xl-checkbox :disabled="disabled" :label="$t('design.toothUpperJaw') || '上颌'" size="large"
                                v-model="upCheckValue" @change="(val) => onChangeUp(val)"/>
                        </span>
                    </div>
                    <div class="img-wrap">
                        <div class="img img-up"></div>
                    </div>
                </div>
                <div class="item item-second" :class="lowerCheckValue ? 'item-active' : ''"
                    @click="() => onToggle('down')">
                    <div>
                        <span @click.stop="onCheckBox">
                            <xl-checkbox :disabled="disabled" :label="$t('design.toothLowerJaw') || '下颌'" size="large"
                                v-model="lowerCheckValue" @change="(val) => onChangeDown(val)"/>
                        </span>
                    </div>
                    <div class="img-wrap">
                        <div class="img img-lower"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div v-else-if="type === 'perCase'" class="all-wrap">
        <!-- 案例详情所需图片 -->
        <div v-if="source === 'caseDetail'" class="detail-img-box">
            <div class="img detail-img">
                <div class="detail-img-text">{{ $t('design.fullJaw') || '全颌' }}</div>
            </div>
        </div>
        <div v-else>
            <xl-radio-group v-model="radioValue">
                <xl-radio label="all" size="large">
                    {{ $t('design.fullJaw') || '全颌' }}
                </xl-radio>
            </xl-radio-group>
            <div class="img"></div>
        </div>
    </div>
    <xl-tooth v-else-if="['perPiece', 'perHole'].includes( type )" ref="toothRef" :type="toothType" :size="214"
        :toothSignTypeDisabled="disabled" :disabled="disabled" :bridgeState="bridgeState"
        v-model:selectTooth="repair.repairtooth" v-model:toothInfo="repair.repairtoothInfo"
        v-model:bridgeInfo="repair.repairbridgeInfo" v-model:toothSignType="repair.toothSignType"
        @change="onToothChange" @update:toothSignType="onToothSignTypeChange" @removeAll="onRemoveAll" :singleDisabledTeeth="props.singleDisabledTeeth" :selectSingleTip="props.selectSingleTip"></xl-tooth>
</template>

<script setup>
import { ref, defineProps, defineExpose, watch, defineEmits, computed } from 'vue';
import { XlTooth, XlCheckbox, XlRadio, XlRadioGroup, XlMessage } from '@shining3d/ui-components';
import { useLang } from "@shining3d/common-module/utils/lang.js";
import { $transToothNumber } from '../../../utils/common.js';
import { toothNameMap } from '../const';
const { $t } = useLang("@shining3d/xl-design-order");

const props = defineProps({
    type: {
        type: String,
        default: '', // perCase: 全颌 perJaw: 上下颌，perPiece 其他选择牙号 perHole 打孔模式
    },
    disabled: {
        type: Boolean,
        default: false
    },
    defaultValue: {
        type: Array // 默认选中的牙号：perJaw 类型时 ['upperJaw', 'lowerJaw']. perPiece 类型时 ['21','22','23']
    },
    toothType: {
        type: String,
        default: '' // 'sign'标记，只选择牙位   'repair'修复类型    'plant'种植类型  hole打孔类型（交互和plant类型类似） 'signFocus'标记聚焦
    },
    toothSignType: {
        type: String,
        default: 'FDI'  // 牙位标记法，'FDI','Universal',默认'FDI'
    },
    bridgeInfo: {
        type: String,
        default: ''
    },
    source: {
        type: String
    },
    singleDisabledTeeth: {
        type: Boolean,
        default: false
    },
    selectSingleTip: {
        type: String,
        default: ''
    }
});
const disabledTeeth = ref([]);
// const selectSingleTip = ref('');
const needSelectSingle = ref( true );
// const singleDisabledTeeth = ref( false );
const emits = defineEmits(['change']);
const bridgeState = ref(false); // false
// 牙号
const repair = ref({
    repairtooth: '',
    repairtoothInfo: [],
    repairbridgeInfo: [],
    toothSignType: props.toothSignType
});

const upCheckValue = ref(false);
const lowerCheckValue = ref(false);
const radioValue = ref('all');

watch(() => [props.disabled, props.defaultValue, props.bridgeInfo], (val) => {
    if (val[0]) {
        if (props.type === 'perJaw') {
            upCheckValue.value = val[1].includes('upperJaw');
            lowerCheckValue.value = val[1].includes('lowerJaw');
        } else if (props.type === 'perPiece' || props.type === 'perHole') {
            repair.value.repairtoothInfo = val[1]?.map(item => {
                return {
                    tooth: item,
                    key: "AnatomicCrown",
                    isPlanted: props.toothType === 'plant' || props.toothType === 'hole'
                }
            });
            let bridgeInfo = [];
            try {
                bridgeInfo = JSON.parse(val[2]);
            } catch (e) { }
            repair.value.repairbridgeInfo = bridgeInfo;
            repair.value.toothSignType = props.toothSignType;
        }
    }
}, { immediate: true, deep: true });

const onCheckBox = () => {};

const onChangeUp = ( val ) => {
    const arr = [];
    let activeTooth = '';
    if ( lowerCheckValue.value ) {
        arr.push('lowerJaw');
    }
    if ( val ) {
        activeTooth = 'upperJaw';
        arr.push('upperJaw');
    }
    emits('change', {
        activeTooth,
        tooths: arr
    });
};

const onChangeDown = ( val ) => {
    const arr = [];
    let activeTooth = '';
    if ( upCheckValue.value ) {
        arr.push('upperJaw');
    }
    if ( val ) {
        activeTooth = 'lowerJaw';
        arr.push('lowerJaw');
    }
    emits('change', {
        activeTooth,
        tooths: arr
    });
};

const onToggle = (type) => {
    if (props.disabled) {
        return;
    }
    if (type === 'up') {
        upCheckValue.value = !upCheckValue.value;
    } else {
        lowerCheckValue.value = !lowerCheckValue.value;
    }
    const arr = [];
    let activeTooth = '';
    if ( type === 'up' ) {
        if ( lowerCheckValue.value ) {
            arr.push('lowerJaw');
        }
        if ( upCheckValue.value ) {
            activeTooth = 'upperJaw';
            arr.push('upperJaw');
        }
    } else {
        if ( upCheckValue.value ) {
            arr.push('upperJaw');
        }
        if ( lowerCheckValue.value ) {
            activeTooth = 'lowerJaw';
            arr.push('lowerJaw');
        }
    }
    // if ( upCheckValue.value ) {
    //     if ( type === 'up' ) {
    //         activeTooth = 'upperJaw';
    //     }
    //     arr.push('upperJaw');
    // } 
    // if ( lowerCheckValue.value ) {
    //     if ( type === 'down' ) {
    //         activeTooth = 'lowerJaw';
    //     }
    //     arr.push('lowerJaw');
    // }
    emits('change', {
        activeTooth,
        tooths: arr
    });
};

const validate = () => {
    let flag = true;
    let message = ''
    if (props.type === 'perPiece' || props.type === 'perHole' || !props.type) {
        const repairtoothInfo = repair.value.repairtoothInfo;
        if (!repairtoothInfo.length) {
            message = $t('design.tooth.choose') || '请选择牙号';
            flag = false;
        }
    } else if (props.type === 'perJaw') {
        flag = upCheckValue.value || lowerCheckValue.value;
        if (!flag) {
            message = $t('design.tooth.uplowJaw') || '请选择上颌或者下颌';
        }
    }
    return {
        valid: flag,
        message
    };
};

const getValue = () => {
    let tooths = [], dentalNotation = '';

    if (props.type === 'perPiece' || props.type === 'perHole' || !props.type) {
        dentalNotation = repair.value.toothSignType;
        tooths = repair.value.repairtoothInfo.map(item => item.tooth);
    } else if (props.type === 'perJaw') {
        if (upCheckValue.value) {
            tooths.push('upperJaw');
        }
        if (lowerCheckValue.value) {
            tooths.push('lowerJaw');
        }
    } else if (props.type === 'perCase') {
        tooths.push('perCase');
    }

    const toothsName = tooths.map(item => {
        let showName = '';
        if (toothNameMap[item]) {
            showName = toothNameMap[item];
        } else {
            showName = $transToothNumber(Number(item), repair.value.toothSignType);
        }
        return showName;
    }).join('、');

    return {
        bridgeInfo: JSON.stringify(repair.value.repairbridgeInfo || []),
        dentalNotation, // 牙位标记
        toothType: props.type, // 牙号选择类型
        tooths, // 牙位信息
        toothsName
    }
};

const resetData = () => {
    repair.value = {
        repairtooth: '',
        repairtoothInfo: [],
        repairbridgeInfo: [],
        toothSignType: 'FDI'
    };
    bridgeState.value = false;
    upCheckValue.value = false;
    lowerCheckValue.value = false;
};

const onToothChange = ( data ) => {
    let tooths = repair.value.repairtoothInfo.map(item => item.tooth);
    let activeTooth = data;
    if ( tooths.includes( data )) {
        activeTooth = data;
    } else {
        activeTooth = '';
    }
    
    if ( props.singleDisabledTeeth ) {
        const allTooths = repair.value.repairtoothInfo;
        repair.value.repairtoothInfo = allTooths.filter(item => item.tooth == data);
        tooths = repair.value.repairtoothInfo.map(item => item.tooth);
    }
    emits('change', {
        activeTooth,
        tooths,
        signType: repair.value.toothSignType
    })
};

const onToothSignTypeChange = (val) => {
    emits('change', {
        tooths: repair.value.repairtoothInfo.map(item => item.tooth),
        signType: val
    });
};

const onRemoveAll = () => {
    emits('change', {
        tooths: [],
        signType: repair.value.toothSignType
    });
    if( props.toothType === 'repair') {
        disabledTeeth.value = [];
    } else {
        disabledTeeth.value = ['18', '28', '38', '48'];
    }
};

//案例详情显示的牙位图
const showJawImg = computed(() => {
    if( lowerCheckValue.value && upCheckValue.value ){
        return 'upper-lower-jaw'
    } else if( lowerCheckValue.value ) {
        return 'lower-jaw'
    } else if ( upCheckValue.value ) {
        return 'upper-jaw'
    } else {
        return ''
    }
})
const fdiTeeth = ['11', '12', '13', '14', '15', '16', '17', '18', '21', '22', '23', '24', '25', '26', '27', '28', '31', '32', '33', '34', '35', '36', '37', '38', '41', '42', '43', '44', '45', '46', '47', '48' ]
watch(() => repair.value, val => {
    if( props.singleDisabledTeeth ){
        // singleDisabledTeeth.value = true;
        // selectSingleTip.value = '请选择单颗牙齿';
        // if( props.toothType === 'repair'){
        //     disabledTeeth.value = [];
        // } else {
        //     disabledTeeth.value = ['18', '28', '38', '48'];
        // }
        if( val?.repairtooth ){
            disabledTeeth.value = fdiTeeth?.filter((item) => item !== val?.repairtooth )
        }
    }
    
},{immediate: true, deep: true})
watch(() => props.toothType, val => {
    if( val === 'repair') {
        disabledTeeth.value = [];
    } else {
        disabledTeeth.value = ['18', '28', '38', '48'];
    }
}, {immediate: true})
defineExpose({ validate, getValue, resetData });
</script>

<style lang="scss" scoped>
.uplower-wrap {
    .item {
        width: 232px;
        height: 244px;
        display: flex;
        box-sizing: border-box;
        border-radius: 12px;
        box-shadow: 0px 2px 10px 0px #111B330D;
        background: #fff;
        padding: 3px 16px 16px 16px;
        margin: 0 auto;
        flex-direction: column;
        border: 1px solid transparent;
        cursor: pointer;
    }

    .item-second {
        margin-top: 16px;
    }

    .img-wrap {
        width: 200px;
        height: 188px;
        background: #F7F8FA;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .img {
        margin: 0 auto;
        background-size: cover;
    }

    .img-up {
        width: 160px;
        height: 163px;
        background-image: url('../../../assets/image/upNew.png');
    }

    .img-lower {
        width: 160px;
        height: 174px;
        background-image: url('../../../assets/image/downNew.png');
    }

    .item-active {
        border: 1px solid #5587FF;
        background: #EDF3FF;

        .img-wrap {
            background: #fff;
        }

        :deep(.is-checked) {
            color: #5587FF;
        }
    }
}

.uplower-wrap-disabled {
    cursor: not-allowed;

    .item {
        cursor: not-allowed;
    }
}

.all-wrap {
    text-align: center;

    .img {
        width: 214px;
        height: 451px;
        margin: 0 auto;
        // margin-top: ;
        background-image: url('../../../assets/image/allnew.png');
        background-size: contain;
        background-repeat: no-repeat;
        margin-top: 8px;
    }

    :deep(.el-radio__input) {
        cursor: none;

        .el-radio__inner {
            border-color: #D4E1FF !important;

            &::after {
                background: #D4E1FF !important;
            }
        }
    }
}

.detail-img-box {
    position: relative;
    .detail-img {
        width: 173px !important;
        height: 365px !important;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }

    .detail-img-text {
        // position: absolute;
        font-size: 12px;
        font-weight: 500;
        // top: 175px;
        // right: 76px;
    }
    // .lower-text{
        // top: 255px !important;
    // }
    .upper-text{
        margin-bottom: 160px;
        // top: 85px !important;
    }
    .disabled-text{
        opacity: 0.1;
    }
    .upper-lower-jaw{
        background-image: url('../../../assets/image/allnew.png');
        background-size: contain;
        background-repeat: no-repeat;
    }
    .upper-jaw{
        background-image: url('../../../assets/image/upperjaw_png.png');
        background-size: contain;
        background-repeat: no-repeat;
    }
    .lower-jaw{
        background-image: url('../../../assets/image/lowerJaw_png.png');
        background-size: contain;
        background-repeat: no-repeat;
    }
}
</style>